/**
 * Module: App | Maintainer: Gaurav Kumar
 * Main application component for initializing the React app.
 *
 * This component handles authentication using MSAL (Microsoft Authentication Library)
 * and applies theming through Chakra UI. It conditionally renders either the authenticated
 * content (`AppInner`) or the authentication flow based on the session state.
 *
 * It also integrates external services like Datadog RUM for user monitoring,
 * and sets up React Query's `QueryClient` for API data fetching.
 *
 * Wraps Application theming, authentication, and routing.
 *
 * @returns {JSX.Element} The root application Component
 */

import { Theme } from '@abi-labs-hexa/web-v2/core/theme';
import '@abi-labs-hexa/web-v2/core/theme/index.css';
import { InteractionStatus, InteractionType, type RedirectRequest } from '@azure/msal-browser';
import type { IMsalContext, MsalAuthenticationResult } from '@azure/msal-react';
import { MsalAuthenticationTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { ChakraProvider } from '@chakra-ui/react';
import { datadogRum } from '@datadog/browser-rum';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type FunctionComponent, lazy, memo, Suspense, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import { AUTOMATION_TOKEN } from 'watchtower-ui/api/api';
import clearStoredAuthToken from 'watchtower-ui/api/authSession';
import getSelectedAccount, { requiresAccountSelection } from 'watchtower-ui/api/msalAccount';
import { FullScreenLoader } from 'watchtower-ui/components/common/Spinner';
import { store } from 'watchtower-ui/redux/store';
import router from 'watchtower-ui/routes';
import theme from 'watchtower-ui/theme';
import { b2cScope } from 'watchtower-ui/utils/Constants';

const Unauthorized = lazy(() => import('watchtower-ui/pages/Unauthorized/Unauthorized'));

const queryClient = new QueryClient();
const accountSelectionRequest: RedirectRequest = {
  scopes: ['openid', 'profile', b2cScope],
  prompt: 'select_account',
};

const AppInner = memo(() => (
  <QueryClientProvider client={queryClient}>
    <Suspense fallback={<FullScreenLoader />}>
      <Provider store={store}>
        <RouterProvider router={router} />
      </Provider>
    </Suspense>
  </QueryClientProvider>
));

const MsalApp = memo(() => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, accounts, instance } = useMsal();
  const [accountSelectionFailed, setAccountSelectionFailed] = useState(false);
  const activeAccount = getSelectedAccount({ accounts, instance });
  const needsAccountSelection =
    isAuthenticated && requiresAccountSelection({ accounts, instance }) && !accountSelectionFailed;

  useEffect(() => {
    if (!needsAccountSelection || inProgress !== InteractionStatus.None) {
      return;
    }

    const startAccountSelection = async () => {
      setAccountSelectionFailed(false);
      clearStoredAuthToken();

      try {
        await instance.loginRedirect(accountSelectionRequest);
      } catch (error) {
        console.error('Failed to start account selection redirect:', error);
        setAccountSelectionFailed(true);
      }
    };

    // eslint-disable-next-line no-void -- Fire redirect without blocking render
    void startAccountSelection();
  }, [inProgress, instance, needsAccountSelection]);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      if (activeAccount) {
        datadogRum.setUser({ email: activeAccount.username, id: activeAccount.username, name: activeAccount.name });
      } else {
        datadogRum.clearUser();
      }
    }
  }, [activeAccount]);

  if (needsAccountSelection) {
    return <FullScreenLoader />;
  }

  if (accountSelectionFailed) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <Unauthorized />
      </Suspense>
    );
  }

  return (
    <>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={accountSelectionRequest}
        loadingComponent={FullScreenLoader as unknown as FunctionComponent<IMsalContext>}
        errorComponent={Unauthorized as unknown as FunctionComponent<MsalAuthenticationResult>}
      >
        <AppInner />
      </MsalAuthenticationTemplate>
      {inProgress === InteractionStatus.None && !isAuthenticated && (
        <UnauthenticatedTemplate>
          <Unauthorized />
        </UnauthenticatedTemplate>
      )}
    </>
  );
});

const App = memo(() => (
  <Theme>
    <ChakraProvider theme={theme}>
      {sessionStorage.getItem(AUTOMATION_TOKEN) ? <AppInner /> : <MsalApp />}
      <ToastContainer />
    </ChakraProvider>
  </Theme>
));
App.displayName = 'App';
export default App;
